import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircleIcon, CheckCircle2Icon, CheckIcon, ChevronDownIcon, CircleXIcon, ClockIcon, FrownIcon, GiftIcon, HelpCircleIcon, QuoteIcon, ShieldAlertIcon, ShieldIcon, SparklesIcon, TrophyIcon, XIcon } from 'lucide-react';
import { bonuses, faqs, testimonials, tomos } from '../data/content';
import { Button } from './ui/Button';
type Testimonial = {
  initials: string;
  name: string;
  place: string;
  text: string;
};
function Eyebrow({
  children,
  danger = false
}: {
  children: React.ReactNode;
  danger?: boolean;
}) {
  return <p className={`mb-3 text-center text-[11px] font-bold uppercase tracking-[.16em] md:text-sm ${danger ? 'text-red-500' : 'text-accent'}`} data-id="element-17" data-mp="1nmjd2trov4uh">
      {children}
    </p>;
}
function SectionTitle({
  children
}: {
  children: React.ReactNode;
}) {
  return <h2 className="mx-auto max-w-3xl text-center text-2xl font-extrabold leading-tight text-slate-900 md:text-4xl" data-id="element-18" data-mp="341ulghuxcqf6">
      {children}
    </h2>;
}
function TestimonialCard({
  item
}: {
  item: Testimonial;
}) {
  return <article className="relative flex flex-col rounded-2xl border border-orange-100 bg-white p-6 shadow-sm" data-id="element-19" data-mp="16rvnwrq26clz">
      <QuoteIcon className="absolute right-5 top-5 h-7 w-7 text-accent/15" data-id="element-20" data-mp="12nfierkcx77a" />
      <div className="mb-4 text-sm text-gold" data-id="element-21" data-mp="xqp6mgfl4xro">★★★★★</div>
      <p className="mb-5 flex-1 text-base font-medium italic leading-relaxed text-slate-700" data-id="element-22" data-mp="xs8i7tqaqkn4">
        “{item.text}”
      </p>
      <div className="flex items-center gap-3 border-t border-orange-100 pt-4" data-id="element-23" data-mp="nzknr5msgsrk">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-accent/20 bg-orange-50 font-display text-sm font-bold text-accent" data-id="element-24" data-mp="j0zq9dyx8n6t">
          {item.initials}
        </span>
        <div data-id="element-25" data-mp="a0uszwnfz56d">
          <p className="text-sm font-bold text-slate-900" data-id="element-26" data-mp="a0l3sba8121q">{item.name}</p>
          <p className="text-xs text-slate-500" data-id="element-27" data-mp="51i7bf8b445d">{item.place}</p>
        </div>
      </div>
    </article>;
}
export function Testimonials() {
  return <section className="mx-auto max-w-6xl px-4 py-14 md:py-16" data-id="element-28" data-mp="3rhfjlv30b9in">
      <Eyebrow data-id="element-29" data-mp="cza3w3rwz4d">⭐ Lo que dicen los que ya lo tienen</Eyebrow>
      <SectionTitle data-id="element-30" data-mp="acr90bfmx6u">
        Profesionales reales que ya aplican <span className="text-accent" data-id="element-31" data-mp="3hm4dg15nzami">el sistema</span>
      </SectionTitle>
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-32" data-mp="3mjdzr7xrb664">
        {testimonials.map(item => <TestimonialCard key={item.initials} item={item} data-id="element-33" data-mp="e3ucqqnoijzi" />)}
      </div>
      <div className="mx-auto mt-8 max-w-3xl rounded-3xl border-2 border-orange-200 bg-white p-6 text-center shadow-lg shadow-orange-100" data-id="element-34" data-mp="j3qfkmkn45dh">
        <p className="text-sm font-bold uppercase tracking-wide text-accent" data-id="element-35" data-mp="4drjcpcnb40n">
          ⏰ Oferta de lanzamiento por tiempo limitado
        </p>
        <p className="mt-2 text-xl font-extrabold text-slate-900" data-id="element-36" data-mp="x2ydzd8zb9gm">
          Sumate hoy y dejá tu programa de adultos mayores{' '}
          <span className="text-accent" data-id="element-37" data-mp="11uq9m26zs5dm">listo en minutos.</span>
        </p>
        <div className="mt-5" data-id="element-38" data-mp="2mju21iko87d3">
          <Button href="#pricing" data-id="element-39" data-mp="2misp18k3hasq">QUIERO VER MI OFERTA DEL COMBO ADULTOS MAYORES</Button>
        </div>
      </div>
    </section>;
}
const pains = ['Repetís siempre los mismos ejercicios porque no tenés un programa estructurado por niveles y semanas.', 'No sabés cómo evaluar a cada alumno ni cómo asignarle un nivel de forma objetiva al inicio.', 'Perdés alumnos porque la clase se vuelve monótona o porque no ven progreso concreto.', 'Invertís 2 o 3 horas buscando ejercicios que podrías tener organizados y listos en 5 minutos.', 'Tenés miedo de lesionar a un alumno porque nunca tuviste un marco claro de adaptaciones y progresiones.', 'No sabés cómo manejar grupos con diferentes capacidades en la misma clase y eso te genera inseguridad constante.', 'No tenés herramientas para medir el progreso de tus alumnos de manera objetiva y demostrable.'];
const transformations = ['Sin buscar ejercicios en internet', 'Progresión real durante 52 semanas', 'Criterios objetivos para asignar niveles', 'Alumnos que se quedan y te recomiendan', 'Adaptaciones claras por patología y nivel'];
export function PainAndTransformation() {
  return <>
      <section className="border-y border-orange-100 bg-white px-4 py-12 md:py-16" data-id="element-40" data-mp="s4f0j9560m67">
        <div className="mx-auto max-w-4xl" data-id="element-41" data-mp="x3rqw3v8wbdj">
          <Eyebrow danger data-id="element-42" data-mp="x5mb1fyozzgk">😤 La realidad</Eyebrow>
          <SectionTitle data-id="element-43" data-mp="123e1e5jovdin">
            ¿Cuántas veces más vas a improvisar con un{' '}
            <span className="text-accent" data-id="element-44" data-mp="121bazkemjide">grupo de adultos mayores delante tuyo?</span>
          </SectionTitle>
          <p className="mx-auto mt-6 max-w-3xl text-center font-medium text-slate-600" data-id="element-45" data-mp="1avpr1p6zawxk">
            Amás lo que hacés. Pero nadie te preparó para esto:
          </p>
          <div className="mt-9 grid gap-3 sm:grid-cols-2 md:gap-4" data-id="element-46" data-mp="1ftrcuhvshacq">
            {pains.map(pain => <div key={pain} className="flex items-start gap-3 rounded-xl border border-orange-100 bg-orange-50/60 p-4" data-id="element-47" data-mp="1krj2f58yokdr">
                <CircleXIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-500" data-id="element-48" data-mp="3abivga5y0i1y" />
                <span className="text-sm font-medium text-slate-700 md:text-base" data-id="element-49" data-mp="35benj8j3ovnc">{pain}</span>
              </div>)}
          </div>
          <div className="mt-10 rounded-2xl border border-accent/20 bg-orange-50 p-6 text-center" data-id="element-50" data-mp="30enyhvpbipps">
            <p className="text-lg font-bold text-slate-700 md:text-2xl" data-id="element-51" data-mp="2vfl5j9mm49y6">
              No es falta de ganas.
              <br data-id="element-52" data-mp="3t33d4h4jf60l" />
              <span className="text-xl text-accent md:text-3xl" data-id="element-53" data-mp="3o2fog23s719j">Es falta de sistema.</span>
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-12 md:py-16" data-id="element-54" data-mp="3j9uwz78cl096">
        <Eyebrow data-id="element-55" data-mp="3ed4hal0gg6oa">
          <span className="inline-flex items-center gap-2" data-id="element-56" data-mp="3ebkyonrbtjjc">
            <SparklesIcon className="h-4 w-4" data-id="element-57" data-mp="fm2ou382cvcv" /> La transformación
          </span>
        </Eyebrow>
        <SectionTitle data-id="element-58" data-mp="avvzg15q96tk">
          ¿Y si la próxima clase fuera{' '}
          <span className="text-accent" data-id="element-59" data-mp="atjb969dm082">completamente diferente?</span>
        </SectionTitle>
        <div className="mt-8 rounded-3xl border border-accent/20 bg-orange-50/70 p-6 shadow-[0_10px_40px_rgba(249,115,22,.10)] md:p-10" data-id="element-60" data-mp="2549jeg4l5f45">
          <p className="text-center text-base font-bold leading-relaxed text-slate-900 md:text-xl" data-id="element-61" data-mp="1vdy3dv7mnfgw">
            Abrís el tomo. Identificás la semana. Dictás la clase. En 5 minutos, la sesión está
            lista.
          </p>
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-id="element-62" data-mp="1mbx833vyf3hv">
            {transformations.map(item => <div key={item} className="rounded-xl border border-accent/20 bg-white p-3 text-center text-xs font-bold text-accent md:text-sm" data-id="element-63" data-mp="1qch5m8kfw94j">
                {item}
              </div>)}
          </div>
          <p className="mt-6 text-center font-medium text-slate-600" data-id="element-64" data-mp="36tv7wrry87z8">
            Eso es el{' '}
            <strong className="text-slate-900" data-id="element-65" data-mp="3bqver4wmhere">
              Combo Completo Entrenamiento para Adultos Mayores.
            </strong>
          </p>
        </div>
        <div className="mt-8 text-center" data-id="element-66" data-mp="v5i1bjmcllei">
          <Button href="#pricing" data-id="element-67" data-mp="v6t1zakhyd1e">QUIERO EL SISTEMA COMBO COMPLETO ADULTOS MAYORES</Button>
        </div>
      </section>
    </>;
}
const values = ['Más de 156 clases desarrolladas', '6 Tomos completos', 'De principiante a avanzado', 'Sesiones listas para dictar', 'Creado para profesionales', 'Adaptado para Argentina y LATAM'];
export function ProductOverview() {
  const [open, setOpen] = useState(0);
  return <section className="mx-auto max-w-5xl px-4 py-12 md:py-16" data-id="element-68" data-mp="1dy2dtqhqpdw5">
      <Eyebrow data-id="element-69" data-mp="1ss74hwsse41y">
        <span className="inline-flex items-center gap-2" data-id="element-70" data-mp="1ssqgkwvr9y82">
          <TrophyIcon className="h-4 w-4" data-id="element-71" data-mp="1ntm9cbse6akd" /> El sistema — Entrenamiento para Adultos Mayores
        </span>
      </Eyebrow>
      <SectionTitle data-id="element-72" data-mp="3uln1rcjdqh5w">
        6 tomos. 52 semanas. <span className="text-accent" data-id="element-73" data-mp="3umg9afq5xap3">Todo el año cubierto.</span>
      </SectionTitle>
      <p className="mt-4 text-center font-medium text-slate-600" data-id="element-74" data-mp="3pnlgqm2e87rs">
        De la evaluación inicial al nivel avanzado II. Todo listo para aplicar hoy.
      </p>

      <div className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-3" data-id="element-75" data-mp="38qec5ar3wph5">
        {values.map(value => <div key={value} className="flex items-center gap-3 rounded-xl border border-orange-100 bg-white p-4 shadow-sm" data-id="element-76" data-mp="3dj7h9zqhlztr">
            <span className="rounded-lg bg-orange-100 p-2" data-id="element-77" data-mp="2kth2doh16twl">
              <CheckIcon className="h-4 w-4 text-accent" data-id="element-78" data-mp="2kv1y3hf30iw1" />
            </span>
            <span className="text-sm font-bold leading-tight text-slate-800" data-id="element-79" data-mp="2ptmut6v4kkfq">{value}</span>
          </div>)}
      </div>

      <div className="mt-16" data-id="element-80" data-mp="2uqdbnwer7k27">
        <h3 className="text-center text-2xl font-extrabold text-slate-900 md:text-4xl" data-id="element-81" data-mp="2yt9vtrjxzo89">
          El material que necesitás para{' '}
          <span className="text-accent" data-id="element-82" data-mp="a0e77wwx3t4c">
            trabajar con adultos mayores con confianza durante todo el año.
          </span>
        </h3>
        <p className="mt-3 text-center text-sm font-medium text-slate-500" data-id="element-83" data-mp="e4vp1qjpvwch">
          Tocá un tomo para ver el contenido
        </p>
        <div className="mt-8 space-y-3" data-id="element-84" data-mp="1uiyhjgf71wdb">
          {tomos.map((tomo, index) => <div key={tomo.name} className={`overflow-hidden rounded-2xl border bg-white transition-colors ${open === index ? 'border-accent shadow-lg shadow-orange-100' : 'border-orange-100'}`} data-id="element-85" data-mp="1psi2jrvq62ou" data-mpi={index}>
              <button className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left md:px-7 md:py-5" aria-expanded={open === index} onClick={() => setOpen(open === index ? -1 : index)} data-id="element-86" data-mp="1fvn7xk2komfu">
                <span className="flex items-center gap-3" data-id="element-87" data-mp="11v2meg59wcin">
                  <span className="text-2xl" data-id="element-88" data-mp="11wnfe1bxvnee">{tomo.icon}</span>
                  <span data-id="element-89" data-mp="316vm4kjg3vz2">
                    <b className="block text-slate-900" data-id="element-90" data-mp="317ew9f2utg2j">
                      Tomo {index + 1} — {tomo.name}
                    </b>
                    <small className="font-bold text-accent" data-id="element-91" data-mp="2554q1nl8q22a">{tomo.period}</small>
                  </span>
                </span>
                <ChevronDownIcon className={`h-5 w-5 shrink-0 text-accent transition-transform ${open === index ? 'rotate-180' : ''}`} data-id="element-92" data-mp="206uuhbvlszg5" />
              </button>
              <AnimatePresence initial={false} data-id="element-93" data-mp="29687geixs65m">
                {open === index && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} data-id="element-94" data-mp="1lgvxhcfsbu29">
                    <ul className="space-y-2 border-t border-orange-100 px-5 py-4 text-sm font-medium text-slate-600 md:px-7" data-id="element-95" data-mp="1v78eiuoe553n">
                      {tomo.content.map(line => <li key={line} className="flex gap-2" data-id="element-96" data-mp="12q5uzou4m99x">
                          <span className="text-accent" data-id="element-97" data-mp="12oky3skcae8w">→</span>
                          {line}
                        </li>)}
                    </ul>
                  </motion.div>}
              </AnimatePresence>
            </div>)}
        </div>
        <div className="mt-9 rounded-2xl border-2 border-dashed border-accent/40 bg-orange-50 p-6 text-center" data-id="element-98" data-mp="3e6y1ebow5acx">
          <p className="text-base font-extrabold text-slate-900 md:text-xl" data-id="element-99" data-mp="30cf1c6wf6ixs">
            Las 52 semanas ya están planificadas.{' '}
            <span className="text-accent" data-id="element-100" data-mp="3t2dzcrsldqel">Solo falta que abras el Tomo 1.</span>
          </p>
          <div className="mt-5" data-id="element-101" data-mp="zmrpuys32lg">
            <Button href="#pricing" className="w-full text-xs md:w-auto md:text-base" data-id="element-102" data-mp="xtluhvzomei">
              DESBLOQUEAR LOS 6 TOMOS AHORA →
            </Button>
          </div>
          <p className="mt-3 text-xs font-medium text-slate-500" data-id="element-103" data-mp="3o3b05nzameej">
            Acceso inmediato · Garantía de 7 días · Mercado Pago
          </p>
        </div>
      </div>
    </section>;
}
const beforeAfterRows = [['Improvisabas cada clase con ejercicios sueltos sin progresión real entre semanas.', 'Abrís el tomo, identificás la semana y dictás la clase. Todo está desarrollado.'], ['Ningún alumno sabía en qué nivel estaba porque nunca habías hecho una evaluación formal.', 'Evaluación inicial completa con 8 tests y clasificación objetiva por nivel desde el primer día.'], ['Sin criterios objetivos de progresión ni de adaptación a cada patología.', 'Progresión real semana a semana durante 52 semanas con adaptaciones incluidas en cada clase.'], ['Alumnos que se aburrían, se lesionaban o simplemente dejaban de venir.', 'Alumnos que mejoran, ven sus resultados en los tests y te recomiendan a sus amigos y médicos.'], ['2 o 3 horas buscando ejercicios antes de cada clase que igual quedaban incompletas.', '5 minutos para tener la sesión completamente lista.'], ['Miedo constante de no saber qué hacer con un alumno con Parkinson, artrosis o prótesis.', 'Confianza real en cada decisión, ejercicio y adaptación que tomás.']];
export function BeforeAfterAndBonuses() {
  return <>
      <section className="border-y border-orange-100 bg-white px-4 py-12 md:py-16" data-id="element-104" data-mp="32snkzd8hdx8a">
        <div className="mx-auto max-w-5xl" data-id="element-105" data-mp="37jnfkbu4o37m">
          <Eyebrow data-id="element-106" data-mp="37kos6kqskxrg">🔄 Antes vs. después</Eyebrow>
          <SectionTitle data-id="element-107" data-mp="37lzx4nvz4bk5">
            No comprás archivos. <span className="text-accent" data-id="element-108" data-mp="3chozeyzdo6k5">Comprás un sistema.</span>
          </SectionTitle>
          <div className="mt-10 grid gap-4 md:grid-cols-2 md:gap-6" data-id="element-109" data-mp="2evrnjkfi6zh6">
            <div className="rounded-2xl border border-red-200 bg-red-50/40 p-6" data-id="element-110" data-mp="2ju1efiu73dwz">
              <h3 className="mb-5 flex items-center gap-2 font-bold text-slate-900" data-id="element-111" data-mp="2jvw4fbkbpw7x">
                <XIcon className="rounded-lg bg-red-100 p-2 text-red-500" size={40} data-id="element-112" data-mp="2orupap6g0w3l" />
                Antes — Sin sistema
              </h3>
              {beforeAfterRows.map(([before]) => <p key={before} className="mb-3 flex gap-2 text-sm font-medium text-slate-600" data-id="element-113" data-mp="42nehuyn8std">
                  <XIcon className="mt-0.5 h-4 w-4 shrink-0 text-red-500" data-id="element-114" data-mp="82p4v7yxhztc" />
                  {before}
                </p>)}
            </div>
            <div className="rounded-2xl border-2 border-accent bg-white p-6 shadow-lg shadow-orange-100" data-id="element-115" data-mp="1orw98ujc3u8">
              <h3 className="mb-5 flex items-center gap-2 font-bold text-slate-900" data-id="element-116" data-mp="glsqpk442cwe">
                <CheckIcon className="rounded-lg bg-orange-100 p-2 text-accent" size={40} data-id="element-117" data-mp="blwnvo75grif" />
                Después — Con el Combo Adultos Mayores
              </h3>
              {beforeAfterRows.map(([, after]) => <p key={after} className="mb-3 flex gap-2 text-sm font-bold text-slate-800" data-id="element-118" data-mp="km2xgyzq97cb">
                  <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent" data-id="element-119" data-mp="zca6ko3uhe0c" />
                  {after}
                </p>)}
            </div>
          </div>
          <p className="mt-10 text-center text-lg font-bold text-slate-700 md:text-2xl" data-id="element-120" data-mp="18covryjxjd94">
            Recuperás <span className="text-accent" data-id="element-121" data-mp="14bdcearnfo9d">tiempo, claridad</span> y la seguridad de
            trabajar con un sistema que funciona.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-12 md:py-16" data-id="element-122" data-mp="1mxb0l323394g">
        <Eyebrow data-id="element-123" data-mp="1rx799flq7oqc">
          <span className="inline-flex items-center gap-2" data-id="element-124" data-mp="1rxpbcsv2kx66">
            <GiftIcon className="h-4 w-4" data-id="element-125" data-mp="1dzilo009y3hl" /> Bonos exclusivos
          </span>
        </Eyebrow>
        <SectionTitle data-id="element-126" data-mp="1ix0yi1hex2uv">
          Además te llevás <span className="text-accent" data-id="element-127" data-mp="1ivzayxj7mnw9">6 bonos siempre incluidos gratis</span>
        </SectionTitle>
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-id="element-128" data-mp="147lauud6hdje">
          {bonuses.map(([number, icon, name, description, value]) => <article key={number} className="flex flex-col rounded-2xl border border-orange-100 bg-white p-5 shadow-sm" data-id="element-129" data-mp="v8rgzbr8f19j">
              <div className="flex gap-3" data-id="element-130" data-mp="z7qkqt188rze">
                <span className="text-3xl" data-id="element-131" data-mp="l9s57kb66pib">{icon}</span>
                <div data-id="element-132" data-mp="lc4uyjn6645x">
                  <small className="font-bold uppercase tracking-wide text-accent" data-id="element-133" data-mp="q8vdreqczdv1">
                    Bono {number}
                  </small>
                  <h3 className="font-bold text-slate-900" data-id="element-134" data-mp="2rz3uryr93s4c">{name}</h3>
                </div>
              </div>
              <p className="my-4 flex-1 text-sm font-medium leading-relaxed text-slate-600" data-id="element-135" data-mp="2n60yvjz046kz">
                {description}
              </p>
              <div className="flex border-t border-orange-100 pt-4 text-sm font-bold" data-id="element-136" data-mp="2e75v0ejfj8ku">
                <span className="text-slate-400 line-through" data-id="element-137" data-mp="3ax6go4oi58xx">Valor: {value}</span>
                <span className="ml-auto text-accent" data-id="element-138" data-mp="3av2djoyfgdyp">GRATIS</span>
              </div>
            </article>)}
        </div>
        <div className="mx-auto mt-10 max-w-2xl rounded-2xl border border-accent/20 bg-orange-50 p-6 text-center" data-id="element-139" data-mp="31w8bhj2buvdr">
          <p className="font-medium text-slate-600" data-id="element-140" data-mp="2wy6qgslcclzn">
            Valor total de bonos: <s className="font-bold text-slate-400" data-id="element-141" data-mp="1qh1skahz9evf">$76.000 ARS</s>
          </p>
          <p className="mt-1 text-lg font-extrabold text-slate-900" data-id="element-142" data-mp="1qeyssyr0w7di">
            Hoy van <span className="text-accent" data-id="element-143" data-mp="1lgx9ccpoq61i">incluidos gratis</span> con tu compra.
          </p>
          <div className="mt-6" data-id="element-144" data-mp="1bfv0j4vtdmbh">
            <Button href="#pricing" fullWidth className="text-xs md:text-sm" data-id="element-145" data-mp="1gdebnvx7tiz7">
              SÍ, QUIERO MIS 6 BONOS GRATIS ANTES DE QUE SUBA EL PRECIO →
            </Button>
          </div>
        </div>
      </section>
    </>;
}
export function SocialAndFAQ() {
  const [open, setOpen] = useState(0);
  return <section className="mx-auto max-w-4xl px-4 py-12 md:py-16" data-id="element-146" data-mp="1u51k0u4fmhyc">
      <Eyebrow data-id="element-147" data-mp="1u4jch1cbkhb4">
        <span className="inline-flex items-center gap-2" data-id="element-148" data-mp="1u75k10z7mwtc">
          <HelpCircleIcon className="h-4 w-4" data-id="element-149" data-mp="1z2kwbohkkknn" /> Preguntas frecuentes
        </span>
      </Eyebrow>
      <SectionTitle data-id="element-150" data-mp="244aave403ggh">
        Lo que más <span className="text-accent" data-id="element-151" data-mp="2414lryvdgekr">nos preguntan</span>
      </SectionTitle>
      <div className="mt-10 space-y-3" data-id="element-152" data-mp="291jzwsnh9q3s">
        {faqs.map(([question, answer], index) => <div key={question} className={`overflow-hidden rounded-2xl border bg-white ${open === index ? 'border-accent/50 shadow-lg shadow-orange-100' : 'border-orange-100'}`} data-id="element-153" data-mp="9qiulvr61yc2" data-mpi={index}>
            <button className="flex w-full items-center justify-between gap-3 p-5 text-left font-bold text-slate-900 md:px-6" onClick={() => setOpen(open === index ? -1 : index)} aria-expanded={open === index} data-id="element-154" data-mp="2gjqei2vjj27n">
              {question}
              <ChevronDownIcon className={`h-5 w-5 shrink-0 text-accent transition-transform ${open === index ? 'rotate-180' : ''}`} data-id="element-155" data-mp="2ld34agbyhf1z" />
            </button>
            <AnimatePresence initial={false} data-id="element-156" data-mp="1soxgfkplv9xx">
              {open === index && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} className="border-t border-orange-100 px-5 py-4 text-sm font-medium leading-relaxed text-slate-600 md:px-6" data-id="element-157" data-mp="26ogdnigvp0vg">
                  {answer}
                </motion.div>}
            </AnimatePresence>
          </div>)}
      </div>
    </section>;
}
const costs = [[ClockIcon, '156 horas por año', '3 horas buscando ejercicios y planificando × 52 semanas. Tiempo que podrías recuperar desde hoy con las 156 clases ya desarrolladas y listas para dictar.'], [AlertCircleIcon, 'Alumnos que no mejoran', 'Sin un protocolo de evaluación inicial y sin criterios claros de progresión por nivel, es difícil demostrar resultados concretos. Y sin resultados concretos, los alumnos no se quedan ni te recomiendan.'], [FrownIcon, 'Alumnos que abandonan', 'Sin variedad, sin progresión visible y sin juegos recreativos que generen disfrute, el abandono es cuestión de tiempo. El sistema incluye los recursos para que tus alumnos quieran volver semana tras semana.'], [ShieldAlertIcon, 'Inseguridad profesional con patologías', 'Sin saber cómo adaptar la clase para un alumno con Parkinson, artrosis, prótesis de cadera u osteoporosis, cada clase con ese alumno genera dudas. El Mega Kit te da exactamente eso: un protocolo claro para cada condición.']] as const;
export function GuaranteeAndCost() {
  return <>
      <section className="border-y border-orange-100 bg-orange-50/70 px-4 py-12 md:py-16" data-id="element-158" data-mp="2dq7261jmtfk9">
        <div className="mx-auto max-w-3xl text-center" data-id="element-159" data-mp="2inz5rstn2q4a">
          <span className="inline-flex rounded-full border-2 border-accent bg-orange-100 p-5 shadow-lg shadow-orange-100" data-id="element-160" data-mp="24p98od6tkqfx">
            <ShieldIcon className="h-10 w-10 text-accent" data-id="element-161" data-mp="28sfdrhi74bkt" />
          </span>
          <Eyebrow data-id="element-162" data-mp="1us4hfyy4a4s9">🛡️ Sin riesgo · 100% garantía</Eyebrow>
          <SectionTitle data-id="element-163" data-mp="1utpfhyjn45uv">
            Probalo 7 días. Si no te sirve,{' '}
            <span className="text-accent" data-id="element-164" data-mp="1zr7otq1kkn3e">te devolvemos el 100%.</span>
          </SectionTitle>
          <div className="mt-7 rounded-2xl border border-orange-100 bg-white p-6 text-left font-medium leading-relaxed text-slate-600 md:p-8" data-id="element-165" data-mp="3p6xbvun9kpim">
            <p data-id="element-166" data-mp="1ihd89kzbqbjt">
              Abrís los tomos.{' '}
              <b className="text-slate-900" data-id="element-167" data-mp="1ii550z4nqfbo">
                Los aplicás en tus clases o en tus sesiones individuales.
              </b>
            </p>
            <p className="mt-3" data-id="element-168" data-mp="1s76leqf46yq6">
              Y si en 7 días sentís que no era lo que esperabas, escribís a
              compradigitalya@gmail.com y te devolvemos el dinero.
            </p>
            <div className="mt-4 flex flex-wrap gap-4 text-sm font-bold text-slate-900" data-id="element-169" data-mp="217uy9hsrut1h">
              {['Sin vueltas', 'Sin formularios eternos', 'Sin preguntas'].map(text => <span key={text} className="flex items-center gap-1.5" data-id="element-170" data-mp="1wauq91cd3g7q">
                  <CheckCircle2Icon className="h-5 w-5 text-accent" data-id="element-171" data-mp="2b5it07qdo0jn" />
                  {text}
                </span>)}
            </div>
          </div>
          <p className="mt-6 text-lg font-bold text-slate-900" data-id="element-172" data-mp="grhw3see1nm7">
            El riesgo no es tuyo. <span className="text-accent" data-id="element-173" data-mp="bt6n1jvx3h51">Es nuestro.</span>
          </p>
          <div className="mt-6 grid grid-cols-3 gap-3 text-center" data-id="element-174" data-mp="buzqyrf5bn9c">
            <Metric value="<1%" label="Tasa de devoluciones" data-id="element-175" data-mp="11idm7a950sc5" />
            <Metric value="+300" label="Profesionales satisfechos" data-id="element-176" data-mp="15i4a967a4ucz" />
            <Metric value="7 días" label="Garantía sin condiciones" data-id="element-177" data-mp="15kh1yb2uaj38" />
          </div>
        </div>
      </section>

      <section className="border-y border-orange-100 bg-white px-4 py-12 md:py-16" data-id="element-178" data-mp="1fgtv97uh2uxg">
        <div className="mx-auto max-w-4xl" data-id="element-179" data-mp="1feh4c56qla89">
          <Eyebrow danger data-id="element-180" data-mp="1kgpw4y7vi68s">⚖️ El costo real</Eyebrow>
          <SectionTitle data-id="element-181" data-mp="1ke3s38k6y8vb">
            ¿Cuánto te cuesta <span className="text-red-500" data-id="element-182" data-mp="1ogqcioej8rih">NO tenerlo?</span>
          </SectionTitle>
          <div className="mt-10 grid gap-3 sm:grid-cols-2" data-id="element-183" data-mp="1t7qa7vvh7dm1">
            {costs.map(([CostIcon, label, cost]) => <div key={label} className="flex gap-3 rounded-xl border border-orange-100 bg-orange-50/50 p-4 shadow-sm" data-id="element-184" data-mp="1y69w5wa2kpee">
                <span className="h-fit rounded-lg border border-red-100 bg-red-50 p-2" data-id="element-185" data-mp="28598bou1n365">
                  <CostIcon className="h-5 w-5 text-red-500" data-id="element-186" data-mp="3snjpxczw4l5q" />
                </span>
                <p data-id="element-187" data-mp="3slgqk2culfoc">
                  <b className="block text-sm text-slate-900" data-id="element-188" data-mp="3nogn7z7dur0x">{label}</b>
                  <span className="text-sm font-medium text-slate-500" data-id="element-189" data-mp="3nqjjh0z4pge3">{cost}</span>
                </p>
              </div>)}
          </div>
          <p className="mt-10 rounded-2xl border border-accent/20 bg-white p-6 text-center text-lg font-bold text-slate-700 shadow-sm md:text-2xl" data-id="element-190" data-mp="3dr2gwsf6bimc">
            La próxima clase puede ser igual.
            <br data-id="element-191" data-mp="a9agqqqvvsd2" />
            <span className="text-accent" data-id="element-192" data-mp="aavc2il0vfbz">O puede ser la primera con un sistema real.</span>
          </p>
          <div className="mt-8 text-center" data-id="element-193" data-mp="5hiyd3hvssy0">
            <Button href="#pricing" className="w-full text-sm md:w-auto md:text-base" data-id="element-194" data-mp="jqynnazw2j9">
              EMPEZAR HOY CON EL SISTEMA COMPLETO →
            </Button>
          </div>
        </div>
      </section>
    </>;
}
function Metric({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="rounded-xl border border-orange-100 bg-white p-3" data-id="element-195" data-mp="dqeqbvlmwi0p">
      <p className="font-display text-xl font-bold text-primary" data-id="element-196" data-mp="dp3o407u7ibp">{value}</p>
      <p className="mt-1 text-[10px] font-bold leading-tight text-slate-500" data-id="element-197" data-mp="iq9h7z5et7lr">{label}</p>
    </div>;
}